import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { requestSuggestion } from "../../../abstract/api/rest";
import { StatusTypes } from "../../../abstract/controller/enum/dnd/StatusTypes";
import * as nodeUtils from "../../../abstract/controller/utils/nodeUtils";
import { isNonEmptyString } from "../../../abstract/controller/utils/systemUtils";
import useRequestContainer from "../../../abstract/hook/useRequestContainer";
import useStatus from "../../../abstract/hook/useStatus";
import { getEndpointAI } from "../../../abstract/redux/slice/endpointSlice";
import { getErrors, getIsExtended, setMetaExtended } from "../../../abstract/redux/slice/metaSlice";
import {
  getIsContextMenuDisplayed,
  getWindowLock,
  setContextMenu,
} from "../../../abstract/redux/slice/overlaySlice";
import { isStatusDragged } from "../../../abstract/redux/slice/statusSlice";
import {
  handleConditionCopy,
  handleConditionRemove,
  handleConditionRename,
  handleConditionUpdateExpression,
} from "../../../abstract/redux/slice/structureSlice";
import * as vendorTooltip from "../../../abstract/vendor/tooltip";
import IconMenuVertical from "../../../themes/icons/IconMenuVertical";
import IconWindowMaximize2 from "../../../themes/icons/IconWindowMaximize2";
import IconWindowMinimize2 from "../../../themes/icons/IconWindowMinimize2";
import Labels from "../../label/Labels";
import Meta from "../../meta/Meta";
import TooltipExpression from "../../tooltip/TooltipExpression";
import TooltipNode from "../../tooltip/TooltipNode";
import { ButtonOverlay } from "../../ui/buttons/ButtonOverlay";
import IconError from "../../ui/icons/IconError";
import Menu from "../../ui/menu/Menu";
import styles from "./ConditionHeader.module.scss";

const _inputExists = "↳ Name is not unique";
const _inputEmpty = "↳ Name is not defined";
const _inputColonStart = "↳ Name cannot start with ':'";
const _inputColonEnd = "↳ Name cannot end with ':'";
const _inputColonDouble = "↳ Name cannot contain '::'";

const _placeholderSuggestion = "Loading suggestion...";
const _placeholderDefault = "Logic name";

const _expressionPlaceholder =
  "To add a new line press 'shift + enter'. To accept changes press 'enter'.";

const ConditionHeaderContext = createContext();

function ConditionHeader({ node, siblingNames, main }) {
  const _dispatch = useDispatch();

  const [_labelError, _setLabelError] = useState("");
  const [_placeholder, _setPlaceholder] = useState(_placeholderDefault);

  const _id = nodeUtils.getID(node);
  const _uuid = nodeUtils.getUUID(node);

  const _errors = useSelector(getErrors(_uuid));
  const _isExtended = useSelector(getIsExtended(_uuid));

  const _name = nodeUtils.getName(node);

  const _status = useStatus(_id);
  const _windowLock = useSelector(getWindowLock);
  const _isContextMenuDisplayed = useSelector(getIsContextMenuDisplayed(_id));
  const [_isMouseOver, _setMouseOver] = useState(false);

  const _label = nodeUtils.getLabel(node) ?? "";
  const _expression = nodeUtils.getExpression(node);
  const _type = nodeUtils.getType(node);

  const _isHidden = isStatusDragged(_status) || _windowLock;

  //---------------------------------------------------------------------

  const clearContextMenu = () => {
    if (_isContextMenuDisplayed) {
      _dispatch(setContextMenu(""));
    }
  };

  const toggleContextMenu = () => {
    _dispatch(setContextMenu(_isContextMenuDisplayed ? "" : _id));
  };

  //---------------------------------------------------------------------

  const getClassForm = () => {
    return `${styles["header__form"]}`;
  };

  const getClassHeader = () => {
    return `${styles["header"]} ${_isExtended ? styles["header--selected"] : ""} ${
      styles["color--default"]
    }`;
  };

  //---------------------------------------------------------------------

  const handleOnMouseOver = (e) => {
    e.stopPropagation();

    if (_windowLock) {
      return;
    }

    _setMouseOver(true);
  };

  const handleOnMouseOut = (e) => {
    e.stopPropagation();

    if (_windowLock) {
      return;
    }

    _setMouseOver(false);
  };

  return (
    <ConditionHeaderContext.Provider
      value={{
        isContextMenuDisplayed: _isContextMenuDisplayed,
        clearContextMenu,
        toggleContextMenu,
        name: _name,
        expression: _expression,
        label: _label,
        id: _id,
        uuid: _uuid,
        node,
        type: _type,
        status: _status,
        placeholder: _placeholder,
        setPlaceholder: _setPlaceholder,
        labelError: _labelError,
        setLabelError: _setLabelError,
        isExtended: _isExtended,
        isMouseOver: _isMouseOver,
        siblingNames,
        main,
        isHidden: _isHidden,
        errors: _errors,
      }}>
      <div id={`head-${_uuid}`} className={getClassHeader()}>
        <div
          id={`head-form-${_uuid}`}
          className={getClassForm()}
          onMouseOver={handleOnMouseOver}
          onMouseOut={handleOnMouseOut}>
          <HeaderInputName />
          <Meta node={node} status={_status} show={_isExtended} />
          <Labels node={node} />
          <div className={styles["style--vertical-filler"]} />
          <WindowOptions />
          <HeaderInputExpression />
        </div>
      </div>
    </ConditionHeaderContext.Provider>
  );
}

function HeaderInputName() {
  const _dispatch = useDispatch();
  const {
    node,
    isExtended,
    status,
    placeholder,
    setPlaceholder,
    labelError,
    setLabelError,
    siblingNames,
    isHidden,
    errors,
  } = useContext(ConditionHeaderContext);

  const _endpointAI = useSelector(getEndpointAI);

  const _id = nodeUtils.getID(node);
  const _uuid = nodeUtils.getUUID(node);
  const _name = nodeUtils.getName(node);
  const _type = nodeUtils.getType(node);

  const _container = useRequestContainer({ id: _id, type: _type });

  const [_inputName, _setInputName] = useState(_name);

  const _nameRef = useRef(null);
  const _abortRef = useRef(null);
  const _suggestionTimeoutRef = useRef(null);

  const [_isLoadingSuggestion, _setIsLoadingSuggestion] = useState(false);

  let _nameValueDefault = "";

  const getClassName = () => {
    return `${styles["header__name-input"]} ${
      isExtended ? styles["color--selected"] : styles["color--default"]
    } ${labelError ? styles["style--stroke"] : ""}`;
  };

  const clearSuggestionTimeout = useCallback(() => {
    if (_suggestionTimeoutRef.current) {
      clearTimeout(_suggestionTimeoutRef.current);
    }

    _setIsLoadingSuggestion(false);
  }, []);

  const actionUpdateNameAccept = useCallback(() => {
    clearSuggestionTimeout();

    if (!labelError && _inputName) {
      _dispatch(handleConditionRename(_id, _inputName));
    }
  }, [clearSuggestionTimeout, labelError, _inputName, _dispatch, _id]);

  const actionUpdateNameCancel = useCallback(() => {
    clearSuggestionTimeout();

    if (_name) {
      _setInputName(_name);
      setLabelError("");
      setPlaceholder(_placeholderDefault);
    } else {
      _dispatch(handleConditionRemove(_id));
    }
  }, [_dispatch, _id, _name, clearSuggestionTimeout, setLabelError, setPlaceholder]);

  const handleInputNameOnChange = (e) => {
    const _value = e.target.value;

    _setInputName(_value);

    if (_value.startsWith(":")) {
      setLabelError(_inputColonStart);
    } else if (_value.endsWith(":")) {
      setLabelError(_inputColonEnd);
    } else if (_value.includes("::")) {
      setLabelError(_inputColonDouble);
    } else if (siblingNames.includes(_value) && _name !== _value) {
      setLabelError(_inputExists);
    } else if (!_value && !_nameValueDefault) {
      setLabelError(_inputEmpty);
    } else {
      setLabelError("");
    }

    setPlaceholder(_placeholderDefault);

    if (labelError === "") {
      if (_value && _value.trim() !== "") {
        if (_nameRef.current.selectionEnd >= _nameRef.current.textLength) {
          suggestNameSuffix(_value);
        } else {
          clearSuggestionTimeout();
        }
      }
    }
  };

  const handleInputNameFocusIn = () => {
    if (!_name && (!_inputName || _inputName.trim() === "")) {
      suggestName();
    } else {
      _nameRef.current.select();
    }
  };

  const handleInputNameFocusOut = () => {
    if (_isLoadingSuggestion) {
      return;
    }

    if (labelError) {
      if (_name) {
        _setInputName(_name);
        _dispatch(handleConditionRename(_id, _name));
      } else {
        _dispatch(handleConditionRemove(_id));
      }
      setLabelError("");
      setPlaceholder(_placeholderDefault);
    } else {
      if (_inputName) {
        _dispatch(handleConditionRename(_id, _inputName));
      } else {
        if (_name) {
          _dispatch(handleConditionRename(_id, _name));
        } else {
          _dispatch(handleConditionRemove(_id));
        }
      }
    }
  };

  const suggestName = useCallback(async () => {
    if (!_endpointAI) {
      return;
    }

    _setIsLoadingSuggestion(true);
    setPlaceholder(_placeholderSuggestion);

    _abortRef.current = new AbortController();

    try {
      const _suggestion = await requestSuggestion({
        controller: _abortRef.current,
        ..._container.request,
      });

      if (_nameRef.current) {
        if (_suggestion.status !== "error" && !_nameRef.current.value) {
          _setInputName(_suggestion.message);

          _nameRef.current.value = _suggestion.message;
          _nameRef.current.select();
        } else {
          setPlaceholder("Suggestion error");
        }
      }
    } catch (error) {}

    clearSuggestionTimeout();
  }, [_container.request, _endpointAI, clearSuggestionTimeout, setPlaceholder]);

  const suggestNameSuffix = useCallback(
    async (prefix) => {
      if (!_endpointAI) {
        return;
      }

      if (_suggestionTimeoutRef.current) {
        return;
      }

      _abortRef.current = new AbortController();

      _suggestionTimeoutRef.current = setTimeout(async () => {
        _setIsLoadingSuggestion(true);

        try {
          const _suggestion = await requestSuggestion({
            controller: _abortRef.current,
            prefix,
            ..._container.request,
          });

          if (_suggestion.status !== "error") {
            _setInputName(_suggestion.message);

            if (_nameRef.current) {
              _nameRef.current.value = _suggestion.message;
              _nameRef.current.setSelectionRange(prefix.length, _suggestion.message.length);
            }
          }
        } catch (error) {}

        clearSuggestionTimeout(false);
      }, 2000);
    },
    [_container.request, _endpointAI, clearSuggestionTimeout]
  );

  useEffect(() => {
    const _target = _nameRef.current;

    const callback = (e) => {
      if (e.code === "Enter") {
        e.preventDefault();

        if (!_isLoadingSuggestion) {
          actionUpdateNameAccept();
        }
      } else if (e.code === "Escape") {
        e.preventDefault();

        actionUpdateNameCancel();
      } else if (e.code === "ArrowRight") {
        if (_target.selectionEnd + 1 >= _target.textLength) {
          suggestNameSuffix(_target.defaultValue);
        }
      } else {
        clearSuggestionTimeout();
      }
    };

    if (_target) {
      _target.addEventListener("keydown", callback);
    }

    return () => (_target ? _target.removeEventListener("keydown", callback) : {});
  }, [
    _isLoadingSuggestion,
    actionUpdateNameAccept,
    actionUpdateNameCancel,
    clearSuggestionTimeout,
    suggestNameSuffix,
  ]);

  useEffect(() => {
    if (status === StatusTypes.DRAGGED) {
      _nameRef.current.blur();
    }
  }, [status]);

  useEffect(() => {
    if (!_nameRef.current) {
      return;
    }

    if (!_name) {
      _nameRef.current.focus();
    } else {
      _nameRef.current.blur();
    }
  }, [_name]);

  return (
    <>
      <div className={styles["icon-error"]}>
        <IconError uuid={_uuid} errors={errors} />
      </div>
      <input
        ref={_nameRef}
        type="text"
        value={_inputName}
        placeholder={placeholder}
        className={`${getClassName()} ${_isLoadingSuggestion ? styles.shimmer : ""}`}
        onChange={handleInputNameOnChange}
        onFocus={handleInputNameFocusIn}
        onBlur={handleInputNameFocusOut}
        id={`head-form-input-name-${_uuid}`}
        data-tooltip-id={`node-${_uuid}`}
        data-tooltip-delay-show={vendorTooltip.getTimeFast()}
      />
      <TooltipNode node={node} handle={`node-${_uuid}`} isHidden={isHidden} />
      <label className={styles["error"]}>{labelError}</label>
      <HeaderIconMeta />
      <HeaderIconOptions />
    </>
  );
}

function HeaderInputExpression() {
  const _dispatch = useDispatch();
  const { node, clearContextMenu, status, isHidden } = useContext(ConditionHeaderContext);

  const _endpointAI = useSelector(getEndpointAI);
  
  const _id = nodeUtils.getID(node);
  const _uuid = nodeUtils.getUUID(node);
  const _name = nodeUtils.getName(node);
  const _type = nodeUtils.getType(node);
  const _expression = nodeUtils.getExpression(node);

  const _container = useRequestContainer({ id: _id, type: `${_type}_expression` });

  const [_inputExpression, _setExpressionValue] = useState(_expression);
  const [_rowsExpression, _setExpressionRows] = useState(_expression.split(/\r?\n|\r|\n/g).length);
  const [_isLoadingSuggestion, _setIsLoadingSuggestion] = useState(false);

  const _nameRef = useRef(null);
  const _suggestionTimeoutRef = useRef(null);
  const _abortRef = useRef(null);

  const getClassInput = () => {
    return `${styles["header__expression-input"]}`;
  };

  const clearSuggestionTimeout = useCallback(() => {
    if (_abortRef.current) {
      _abortRef.current.abort();
      _abortRef.current = null;
    }

    if (_suggestionTimeoutRef.current) {
      clearTimeout(_suggestionTimeoutRef.current);
      _suggestionTimeoutRef.current = null;
    }

    _setIsLoadingSuggestion(false);
  }, []);

  const actionUpdateExpressionAccept = useCallback(() => {
    clearSuggestionTimeout();

    _dispatch(handleConditionUpdateExpression(_id, _inputExpression));
  }, [_dispatch, _id, _inputExpression, clearSuggestionTimeout]);

  const actionUpdateExpressionCancel = useCallback(() => {
    clearSuggestionTimeout();

    _nameRef.current.blur();
  }, [clearSuggestionTimeout]);

  const handleInputExpressionOnChange = (e) => {
    const _value = e.target.value;

    _setExpressionValue(_value);
    _setExpressionRows(_value.split(/\r?\n|\r|\n/g).length);
  };

  const handleInputExpressionFocusIn = () => {
    clearContextMenu();

    if (!_expression && (!_inputExpression || _inputExpression.trim() === "")) {
      suggestExpression();
    } else {
      _nameRef.current.select();
    }
  };

  const handleInputExpressionFocusOut = () => {
    clearSuggestionTimeout();

    actionUpdateExpressionAccept();
  };

  const suggestExpression = useCallback(async () => {
    if (!_endpointAI) {
      return;
    }
    _setIsLoadingSuggestion(true);

    _abortRef.current = new AbortController();

    try {
      const _suggestion = await requestSuggestion({
        controller: _abortRef.current,
        ..._container.request,
      });

      if (_nameRef.current) {
        if (_suggestion.status !== "error" && !_nameRef.current.value) {
          _setExpressionValue(_suggestion.message);

          _nameRef.current.value = _suggestion.message;

          _nameRef.current.select();
        }
      }
    } catch (error) {}

    _setIsLoadingSuggestion(false);
  }, [_container.request, _endpointAI]);

  const suggestExpressionSuffix = useCallback(
    async (prefix) => {
      if (!_endpointAI) {
        return;
      }

      if (_suggestionTimeoutRef.current) {
        return;
      }

      _abortRef.current = new AbortController();

      _suggestionTimeoutRef.current = setTimeout(async () => {
        _setIsLoadingSuggestion(true);

        try {
          const _suggestion = await requestSuggestion({
            controller: _abortRef.current,
            prefix,
            ..._container.request,
          });

          if (_suggestion.status !== "error") {
            _setExpressionValue(_suggestion.message);

            if (_nameRef.current) {
              _nameRef.current.value = _suggestion.message;
              _nameRef.current.setSelectionRange(prefix.length, _suggestion.message.length);
            }
          }
        } catch (error) {}

        _setIsLoadingSuggestion(false);
        _suggestionTimeoutRef.current = null;
      }, 1000);
    },
    [_container.request, _endpointAI]
  );

  // useEffect(() => {
  //   _nameRef.current.blur();
  // }, [_expression]);

  useEffect(() => {
    if (_name && !_expression) {
      _nameRef.current.focus();
    }
  }, [_name, _expression]);

  useEffect(() => {
    const _target = _nameRef.current;

    const callback = (e) => {
      if (e.code === "Enter") {
        if (!_isLoadingSuggestion) {
          actionUpdateExpressionAccept();
        }
      } else if (e.code === "Escape") {
        actionUpdateExpressionCancel();
      } else if (e.code === "ArrowRight") {
        if (_target.selectionEnd + 1 >= _target.textLength) {
          suggestExpressionSuffix(_target.defaultValue);
        }
      } else {
        clearSuggestionTimeout();
      }
    };

    if (_target) {
      _target.addEventListener("keydown", callback);
    }

    return () => (_target ? _target.removeEventListener("keydown", callback) : {});
  }, [
    _isLoadingSuggestion,
    actionUpdateExpressionAccept,
    actionUpdateExpressionCancel,
    clearSuggestionTimeout,
    suggestExpressionSuffix,
  ]);

  useEffect(() => {
    if (status === StatusTypes.DRAGGED) {
      _nameRef.current.blur();
    }
  }, [status]);

  return (
    <>
      <textarea
        rows={_rowsExpression}
        id={`head-form-input-expression-${_uuid}`}
        ref={_nameRef}
        type="text"
        className={`${getClassInput()} ${_isLoadingSuggestion ? styles.shimmer : ""}`}
        value={_inputExpression}
        placeholder={_isLoadingSuggestion ? "Loading suggestion..." : _expressionPlaceholder}
        onChange={handleInputExpressionOnChange}
        onFocus={handleInputExpressionFocusIn}
        onBlur={handleInputExpressionFocusOut}
        data-tooltip-id={`node-expression-${_uuid}`}
        data-tooltip-delay-show={vendorTooltip.getTimeFast()}
      />
      <TooltipExpression node={node} handle={`node-expression-${_uuid}`} isHidden={isHidden} />
    </>
  );
}

function HeaderIconMeta() {
  const _dispatch = useDispatch();
  const { uuid, name, status, isExtended, isMouseOver } = useContext(ConditionHeaderContext);

  const getClassIcon = () => {
    return `${styles["icons-meta"]}`;
  };

  const handleButtonShowOptions = () => {
    _dispatch(setMetaExtended(uuid, !isExtended));
  };

  if (!isMouseOver || status === StatusTypes.DRAGGED || !isNonEmptyString(name)) {
    return null;
  }

  return (
    <div className={getClassIcon()}>
      <ButtonOverlay handler={handleButtonShowOptions}>
        {isExtended ? <IconWindowMinimize2 /> : <IconWindowMaximize2 />}
      </ButtonOverlay>
    </div>
  );
}

function HeaderIconOptions() {
  const { name, uuid, toggleContextMenu, status, isMouseOver } = useContext(ConditionHeaderContext);

  const getClass = () => {
    return `${styles["icons-options"]}`;
  };

  const handleMenuRightToggle = () => {
    toggleContextMenu();
  };

  if (!isMouseOver || status === StatusTypes.DRAGGED || !isNonEmptyString(name)) {
    return null;
  }

  return (
    <div className={getClass()} onClick={handleMenuRightToggle}>
      <IconMenuVertical id={`action-${uuid}`} />
    </div>
  );
}

//--------------------------------------------------

function WindowOptions() {
  const _dispatch = useDispatch();
  const { isContextMenuDisplayed, id, clearContextMenu, main } = useContext(ConditionHeaderContext);
  const _isLocked = useSelector(getWindowLock);

  const handleMenuRemove = () => {
    _dispatch(handleConditionRemove(id));
  };

  const handleMenuCopy = () => {
    _dispatch(handleConditionCopy(id));
    clearContextMenu();
  };

  const getActions = () => {
    const _actions = [];

    _actions.push({ name: "remove", action: handleMenuRemove });
    _actions.push({ name: "copy", action: handleMenuCopy });

    return _actions;
  };

  if (_isLocked) {
    return null;
  }

  return (
    <Menu show={isContextMenuDisplayed} items={getActions()} position={main ? "left" : "right"} />
  );
}

export default ConditionHeader;
